import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const NotesContainer = styled.div`
  height: 100%;
  background-color: #1E1E1E;
  color: white;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1E1E1E;
  }

  &::-webkit-scrollbar-thumb {
    background: #444;
    border-radius: 4px;
  }
`;

const NotesList = styled.div`
  width: 90%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const NoteItem = styled.div`
  background: linear-gradient(145deg, #2a2a2a, #232323);
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    background: linear-gradient(145deg, #2f2f2f, #282828);
  }

  &:active {
    transform: translateY(0);
  }
`;

const NoteTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.9);
`;

const NotePreview = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Editor = styled.div`
  background: linear-gradient(145deg, #2a2a2a, #232323);
  border-radius: 16px;
  padding: 12px;
  margin-bottom: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.05);
  width: 90%;
`;

const TitleInput = styled.input`
  width: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 16px;
  padding: 10px;
  margin-bottom: 8px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    background-color: rgba(0, 0, 0, 0.3);
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.3);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const ContentTextarea = styled.textarea`
  width: 100%;
  height: 100px;
  background-color: rgba(0, 0, 0, 0.2);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  line-height: 1.6;
  padding: 10px;
  resize: none;
  margin-bottom: 8px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    background-color: rgba(0, 0, 0, 0.3);
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.3);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 10px;
  justify-content: center;
`;

const Button = styled.button`
  background: linear-gradient(145deg, #4CAF50, #45a049);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  width: 120px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const DeleteButton = styled(Button)`
  background: linear-gradient(145deg, #f44336, #da190b);
  box-shadow: 0 2px 8px rgba(244, 67, 54, 0.2);

  &:hover {
    box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
  }
`;

const Notes = () => {
  const [notes, setNotes] = useState([]);
  const [currentNote, setCurrentNote] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  useEffect(() => {
    const savedNotes = localStorage.getItem('notes');
    if (savedNotes) {
      setNotes(JSON.parse(savedNotes));
    }
  }, []);

  const saveNote = () => {
    if (!title.trim() && !content.trim()) return;

    const newNote = {
      id: currentNote?.id || Date.now(),
      title: title.trim() || 'Untitled',
      content,
      lastModified: new Date().toISOString()
    };

    let updatedNotes;
    if (currentNote) {
      updatedNotes = notes.map(note => 
        note.id === currentNote.id ? newNote : note
      );
    } else {
      updatedNotes = [newNote, ...notes];
    }

    setNotes(updatedNotes);
    localStorage.setItem('notes', JSON.stringify(updatedNotes));
    clearEditor();
  };

  const deleteNote = () => {
    if (!currentNote) return;
    
    const updatedNotes = notes.filter(note => note.id !== currentNote.id);
    setNotes(updatedNotes);
    localStorage.setItem('notes', JSON.stringify(updatedNotes));
    clearEditor();
  };

  const clearEditor = () => {
    setCurrentNote(null);
    setTitle('');
    setContent('');
  };

  const editNote = (note) => {
    setCurrentNote(note);
    setTitle(note.title);
    setContent(note.content);
  };

  return (
    <NotesContainer>
      <Editor>
        <TitleInput
          placeholder="Note title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <ContentTextarea
          placeholder="Start typing..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
        <ButtonContainer>
          <Button onClick={saveNote}>
            {currentNote ? 'Update Note' : 'Save Note'}
          </Button>
          {currentNote && (
            <DeleteButton onClick={deleteNote}>Delete Note</DeleteButton>
          )}
        </ButtonContainer>
      </Editor>

      <NotesList>
        {notes.map(note => (
          <NoteItem key={note.id} onClick={() => editNote(note)}>
            <NoteTitle>{note.title}</NoteTitle>
            <NotePreview>{note.content}</NotePreview>
          </NoteItem>
        ))}
      </NotesList>
    </NotesContainer>
  );
};

export default Notes;